import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export type NotifChannel = "instant" | "every_15m" | "hourly" | "daily_digest" | "off";

export const NOTIF_CHANNEL_LABELS: Record<NotifChannel, string> = {
  instant: "Instant",
  every_15m: "Every 15 min",
  hourly: "Hourly digest",
  daily_digest: "Daily digest",
  off: "Off",
};

export interface NotificationPreferences {
  user_id: string;
  timezone: string;
  quiet_start: string | null;
  quiet_end: string | null;
  quiet_allow_urgent: boolean;
  matches_channel: NotifChannel;
  messages_channel: NotifChannel;
  community_channel: NotifChannel;
  events_channel: NotifChannel;
  broadcasts_channel: NotifChannel;
  raela_channel: NotifChannel;
  updated_at: string;
}

const guessTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

export function useNotificationPreferences() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);

  const defaults = useCallback(
    (uid: string): NotificationPreferences => ({
      user_id: uid,
      timezone: guessTz(),
      quiet_start: null,
      quiet_end: null,
      quiet_allow_urgent: true,
      matches_channel: "instant",
      messages_channel: "instant",
      community_channel: "every_15m",
      events_channel: "instant",
      broadcasts_channel: "instant",
      raela_channel: "instant",
      updated_at: new Date().toISOString(),
    }),
    [],
  );

  useEffect(() => {
    if (!user) {
      setPrefs(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("notification_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      setPrefs((data as NotificationPreferences) ?? defaults(user.id));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, defaults]);

  const update = useCallback(
    async (patch: Partial<NotificationPreferences>) => {
      if (!user) return { error: "not_signed_in" };
      const next = {
        ...(prefs ?? defaults(user.id)),
        ...patch,
        user_id: user.id,
        updated_at: new Date().toISOString(),
      };
      setPrefs(next);
      const { error } = await supabase
        .from("notification_preferences")
        .upsert(next, { onConflict: "user_id" });
      return { error: error?.message ?? null };
    },
    [user, prefs, defaults],
  );

  return { prefs, loading, update };
}
